import React, { useEffect, useState } from "react";
import { Button, Popconfirm, message, Switch, Tooltip, Popover } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, UndoOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AppModal from "../../../Components/Modals/AppModal/AppModal";
import {
  AptPackage, AptPackageChildRule, AptPackageInclude, AptPackagePricingTier, ClientApartment,
  getAptPackages, createAptPackage, updateAptPackage, deleteAptPackage,
} from "../../../utils/api/apartmentRentalsAPI";

interface Props {
  clientId: string;
  apartments: ClientApartment[];
}

const fmtDate = (s: string) => dayjs(s).format("DD.MM.YYYY");

const fmtAmount = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${new Intl.NumberFormat("de-DE").format(n)}`;
  if (currency === "BAM") return `${new Intl.NumberFormat("bs-BA").format(n)} KM`;
  return `${new Intl.NumberFormat("sr-RS").format(n)} RSD`;
};

// Curated icon choices for "šta akcija uključuje" stavke — korisnik bira ikonicu
// ručno umjesto da se ona nagađa iz teksta, tako da kalendar prikazuje tačno
// ono što je dodijeljeno.
const PACKAGE_ICON_OPTIONS: { icon: string; label: string }[] = [
  { icon: "🍳", label: "Doručak" },
  { icon: "🍲", label: "Ručak" },
  { icon: "🍽️", label: "Večera" },
  { icon: "🥂", label: "Piće / welcome drink" },
  { icon: "🏍️", label: "Kvad / ATV / buggy" },
  { icon: "🚤", label: "Izlet / bootska tura" },
  { icon: "🗺️", label: "Tura / obilazak" },
  { icon: "🧖", label: "Spa / wellness" },
  { icon: "🚗", label: "Transfer" },
  { icon: "🅿️", label: "Parking" },
  { icon: "🛎️", label: "Rana prijava / kasna odjava" },
  { icon: "🎁", label: "Ostalo" },
];

const isExpired = (pkg: AptPackage) => dayjs(pkg.validTo).isBefore(dayjs().startOf("day"));
const isUpcoming = (pkg: AptPackage) => dayjs(pkg.validFrom).isAfter(dayjs().startOf("day"));

const ChevDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const GiftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <line x1="12" y1="22" x2="12" y2="7" />
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
  </svg>
);

// ─── Form state ──────────────────────────────────────────────────────────────

const emptyForm = {
  name: "",
  description: "",
  apartmentId: "",
  validFrom: "",
  validTo: "",
  minNights: "1",
  priceType: "discount" as "discount" | "custom",
  discountPercent: "",
  currency: "EUR" as "EUR" | "RSD" | "BAM",
  isActive: true,
};

const defaultPricingTiers = (): Array<{ persons: string; pricePerNight: string }> => [
  { persons: "2", pricePerNight: "" },
  { persons: "4", pricePerNight: "" },
];

type FormData = typeof emptyForm;
type Errors = Partial<Record<keyof FormData | "includes" | "pricePerNight", string>>;

// ─── Main Component ───────────────────────────────────────────────────────────

const PackagesTab: React.FC<Props> = ({ clientId, apartments }) => {
  const [packages, setPackages] = useState<AptPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<AptPackage | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [includes, setIncludes] = useState<AptPackageInclude[]>([{ label: "" }]);
  const [pricingTiers, setPricingTiers] = useState(defaultPricingTiers());
  const [childrenRules, setChildrenRules] = useState<Array<{ ageFrom: string; ageTo: string; priceType: "free" | "fixed" | "adult"; pricePerNight: string }>>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [iconPickerOpen, setIconPickerOpen] = useState<number | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAptPackages(clientId);
      setPackages(res.data);
    } catch {
      message.error("Greška pri učitavanju akcija");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId]);

  const setField = (key: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.type === "checkbox"
      ? (e.target as HTMLInputElement).checked
      : e.target.value;
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): Errors => {
    const e: Errors = {};
    if (!formData.name.trim()) e.name = "Naziv je obavezan";
    if (!formData.validFrom) e.validFrom = "Datum početka je obavezan";
    if (!formData.validTo) e.validTo = "Datum kraja je obavezan";
    if (formData.validFrom && formData.validTo && formData.validFrom > formData.validTo)
      e.validTo = "Datum kraja mora biti nakon početka";
    if (!formData.minNights || Number(formData.minNights) < 1)
      e.minNights = "Min. noći mora biti ≥ 1";
    if (formData.priceType === "custom") {
      const validTiers = pricingTiers.filter((t) => t.persons && t.pricePerNight);
      if (validTiers.length === 0) e.pricePerNight = "Dodajte barem jednu cijenu po broju osoba";
    }
    if (formData.priceType === "discount" && !formData.discountPercent)
      e.discountPercent = "Unesite procenat";
    const validIncludes = includes.filter((i) => i.label.trim());
    if (validIncludes.length === 0) e.includes = "Dodajte barem jednu stavku";
    return e;
  };

  const openCreate = () => {
    setEditData(null);
    setFormData(emptyForm);
    setIncludes([{ label: "" }]);
    setPricingTiers(defaultPricingTiers());
    setChildrenRules([]);
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (pkg: AptPackage) => {
    setEditData(pkg);
    const hasByPersons = pkg.pricingByPersons && pkg.pricingByPersons.length > 0;
    setFormData({
      name: pkg.name,
      description: pkg.description || "",
      apartmentId: pkg.apartmentId ? String(pkg.apartmentId) : "",
      validFrom: pkg.validFrom || "",
      validTo: pkg.validTo || "",
      minNights: String(pkg.minNights ?? 1),
      priceType: (hasByPersons || pkg.pricePerNight != null) ? "custom" : "discount",
      discountPercent: pkg.discountPercent != null ? String(pkg.discountPercent) : "",
      currency: pkg.currency || "EUR",
      isActive: pkg.isActive,
    });
    setPricingTiers(
      hasByPersons
        ? pkg.pricingByPersons!.map((t) => ({ persons: String(t.persons), pricePerNight: String(t.pricePerNight) }))
        : defaultPricingTiers()
    );
    setIncludes(pkg.includes?.length ? pkg.includes : [{ label: "" }]);
    setChildrenRules(
      pkg.childrenPricing?.length
        ? pkg.childrenPricing.map((r) => ({
            ageFrom: String(r.ageFrom),
            ageTo: r.ageTo != null ? String(r.ageTo) : "",
            priceType: r.priceType,
            pricePerNight: r.pricePerNight != null ? String(r.pricePerNight) : "",
          }))
        : []
    );
    setErrors({});
    setModalOpen(true);
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setSaving(true);
    try {
      const validIncludes = includes.filter((i) => i.label.trim());
      const apt = apartments.find((a) => a._id === formData.apartmentId);
      const validTiers: AptPackagePricingTier[] = formData.priceType === "custom"
        ? pricingTiers
            .filter((t) => t.persons && t.pricePerNight)
            .map((t) => ({ persons: Number(t.persons), pricePerNight: Number(t.pricePerNight) }))
            .sort((a, b) => a.persons - b.persons)
        : [];
      const validChildrenRules: AptPackageChildRule[] = childrenRules
        .filter((r) => r.ageFrom !== "")
        .map((r) => ({
          ageFrom: Number(r.ageFrom),
          ageTo: r.ageTo !== "" ? Number(r.ageTo) : null,
          priceType: r.priceType,
          pricePerNight: r.priceType === "fixed" && r.pricePerNight !== "" ? Number(r.pricePerNight) : null,
        }));

      const payload: Partial<AptPackage> = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        apartmentId: formData.apartmentId || null,
        apartmentName: apt?.name || undefined,
        validFrom: formData.validFrom,
        validTo: formData.validTo,
        minNights: Number(formData.minNights),
        includes: validIncludes,
        pricePerNight: null,
        pricingByPersons: formData.priceType === "custom" ? validTiers : [],
        childrenPricing: validChildrenRules,
        discountPercent: formData.priceType === "discount" ? Number(formData.discountPercent) : null,
        currency: formData.currency,
        isActive: formData.isActive,
      };
      if (editData) {
        const res = await updateAptPackage(editData._id, payload);
        setPackages((prev) => prev.map((p) => p._id === editData._id ? res.data : p));
      } else {
        const res = await createAptPackage(clientId, payload);
        setPackages((prev) => [res.data, ...prev]);
      }
      setModalOpen(false);
      message.success(editData ? "Akcija ažurirana" : "Akcija kreirana");
    } catch (err: any) {
      if (err?.response?.data?.message) message.error(err.response.data.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAptPackage(id);
      setPackages((prev) => prev.filter((p) => p._id !== id));
      message.success("Akcija arhivirana");
    } catch {
      message.error("Greška pri arhiviranju");
    }
  };

  const handleToggleActive = async (pkg: AptPackage) => {
    try {
      const res = await updateAptPackage(pkg._id, { isActive: !pkg.isActive });
      setPackages((prev) => prev.map((p) => p._id === pkg._id ? res.data : p));
    } catch {
      message.error("Greška");
    }
  };

  const handleRestore = async (pkg: AptPackage) => {
    try {
      const res = await updateAptPackage(pkg._id, { archived: false, isActive: true });
      setPackages((prev) => prev.map((p) => p._id === pkg._id ? res.data : p));
      message.success("Akcija vraćena");
    } catch {
      message.error("Greška pri vraćanju");
    }
  };

  const addInclude = () => setIncludes((prev) => [...prev, { label: "" }]);
  const removeInclude = (i: number) => setIncludes((prev) => prev.filter((_, idx) => idx !== i));
  const updateInclude = (i: number, label: string) => {
    setIncludes((prev) => prev.map((inc, idx) => idx === i ? { ...inc, label } : inc));
    if (errors.includes) setErrors((prev) => ({ ...prev, includes: undefined }));
  };
  const updateIncludeIcon = (i: number, icon: string | undefined) => {
    setIncludes((prev) => prev.map((inc, idx) => idx === i ? { ...inc, icon } : inc));
  };

  // The backend now returns archived packages too (reservations that reference
  // them still need to resolve their name/includes elsewhere), but this tab
  // is package *management* — archived ones stay hidden here just like before.
  const visiblePackages   = packages.filter((p) => !p.archived);
  const activePackages    = visiblePackages.filter((p) => p.isActive && !isExpired(p));
  const inactivePackages  = visiblePackages.filter((p) => !p.isActive && !isExpired(p));
  const expiredPackages   = visiblePackages.filter((p) => isExpired(p));
  const archivedPackages  = packages.filter((p) => p.archived);

  const selectCls = (val: string, err?: string) =>
    `am-select${!val ? " am-placeholder" : ""}${err ? " am-input-error" : ""}`;

  return (
    <div style={{ padding: "16px 0" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1520" }}>Akcije i paketi</div>
          <div style={{ fontSize: 12, color: "#9893a8", marginTop: 2 }}>
            Paketi koji se primjenjuju kada cijeli boravak pada unutar perioda akcije
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {archivedPackages.length > 0 && (
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                height: 36, padding: "0 12px",
                border: "1px solid #e8e5f0", borderRadius: 9,
                background: "#fff", color: "#6b6880",
                cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                fontFamily: "inherit", whiteSpace: "nowrap",
              }}
            >
              {showArchived ? "Sakrij arhivirane" : `Prikaži arhivirane (${archivedPackages.length})`}
            </button>
          )}
          <Button
            type="primary" icon={<PlusOutlined />} onClick={openCreate}
            style={{ background: "#1a1520", borderColor: "#1a1520" }}
          >
            Nova akcija
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {visiblePackages.length === 0 && !loading && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#9893a8" }}>
          <div style={{ fontSize: 42, marginBottom: 12 }}>🎁</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1520", marginBottom: 6 }}>Nema akcija</div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>
            Kreirajte akciju kao npr. "3 noći + doručak + vožnja kvadom"
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
            style={{ background: "#1a1520", borderColor: "#1a1520" }}>
            Kreiraj prvu akciju
          </Button>
        </div>
      )}

      {/* Active */}
      {activePackages.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#5b50d6", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Aktivne akcije ({activePackages.length})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
            {activePackages.map((pkg) => (
              <PackageCard key={pkg._id} pkg={pkg} onEdit={openEdit} onDelete={handleDelete} onToggle={handleToggleActive} />
            ))}
          </div>
        </div>
      )}

      {/* Inactive */}
      {inactivePackages.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Neaktivne ({inactivePackages.length})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
            {inactivePackages.map((pkg) => (
              <PackageCard key={pkg._id} pkg={pkg} onEdit={openEdit} onDelete={handleDelete} onToggle={handleToggleActive} />
            ))}
          </div>
        </div>
      )}

      {/* Expired */}
      {expiredPackages.length > 0 && (
        <div style={{ marginBottom: showArchived && archivedPackages.length > 0 ? 24 : 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Istekle ({expiredPackages.length})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14, opacity: 0.65 }}>
            {expiredPackages.map((pkg) => (
              <PackageCard key={pkg._id} pkg={pkg} onEdit={openEdit} onDelete={handleDelete} onToggle={handleToggleActive} />
            ))}
          </div>
        </div>
      )}

      {/* Archived */}
      {showArchived && archivedPackages.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Arhivirane ({archivedPackages.length})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14, opacity: 0.75 }}>
            {archivedPackages.map((pkg) => (
              <PackageCard key={pkg._id} pkg={pkg} onEdit={openEdit} onDelete={handleDelete} onToggle={handleToggleActive} onRestore={handleRestore} archived />
            ))}
          </div>
        </div>
      )}

      {/* ── Modal ── */}
      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        icon={<GiftIcon />}
        eyebrow="Akcije i paketi"
        title={editData ? "Izmijeni akciju" : "Nova akcija / paket"}
        description="Definiši period, uvjete i šta akcija uključuje."
        onSubmit={handleSave}
        submitLabel={saving ? "Čuvanje..." : editData ? "Sačuvaj izmjene" : "Kreiraj akciju"}
        submitDisabled={saving}
        maxWidth={600}
      >
        {/* ── Osnovno ── */}
        <div className="am-section">
          <div className="am-section-label">Osnovno</div>
          <div className="am-form-grid">

            <div className="am-field am-span-2">
              <label className="am-label">Naziv akcije <span className="am-req">*</span></label>
              <input
                className={`am-input${errors.name ? " am-input-error" : ""}`}
                placeholder='npr. Romantični vikend paket'
                value={formData.name}
                onChange={setField("name")}
              />
              {errors.name && <span className="am-error-msg">{errors.name}</span>}
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">
                Opis
                <span className="am-field-hint">opciono</span>
              </label>
              <textarea
                className="am-textarea"
                rows={2}
                placeholder="Kratki opis akcije za goste..."
                value={formData.description}
                onChange={setField("description")}
              />
            </div>

            <div className="am-field am-span-2">
              <label className="am-label">
                Apartman
                <span className="am-field-hint">prazno = važi za sve</span>
              </label>
              <div className="am-select-wrap">
                <select
                  className={selectCls(formData.apartmentId)}
                  value={formData.apartmentId}
                  onChange={setField("apartmentId")}
                >
                  <option value="">Svi apartmani</option>
                  {apartments.map((a) => (
                    <option key={a._id} value={a._id}>{a.name}</option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Period i uvjeti ── */}
        <div className="am-section">
          <div className="am-section-label">Period i uvjeti</div>
          <div className="am-form-grid">

            <div className="am-field">
              <label className="am-label">Važi od <span className="am-req">*</span></label>
              <input
                className={`am-input am-date${errors.validFrom ? " am-input-error" : ""}`}
                type="date"
                value={formData.validFrom}
                onChange={setField("validFrom")}
              />
              {errors.validFrom && <span className="am-error-msg">{errors.validFrom}</span>}
            </div>

            <div className="am-field">
              <label className="am-label">Važi do <span className="am-req">*</span></label>
              <input
                className={`am-input am-date${errors.validTo ? " am-input-error" : ""}`}
                type="date"
                value={formData.validTo}
                min={formData.validFrom || undefined}
                onChange={setField("validTo")}
              />
              {errors.validTo && <span className="am-error-msg">{errors.validTo}</span>}
            </div>

            <div className="am-field">
              <label className="am-label">
                Minimalan broj noći <span className="am-req">*</span>
              </label>
              <input
                className={`am-input${errors.minNights ? " am-input-error" : ""}`}
                type="number"
                min={1}
                placeholder="npr. 3"
                value={formData.minNights}
                onChange={setField("minNights")}
              />
              {errors.minNights && <span className="am-error-msg">{errors.minNights}</span>}
            </div>
          </div>
        </div>

        {/* ── Šta uključuje ── */}
        <div className="am-section">
          <div className="am-section-label">Šta akcija uključuje <span className="am-req">*</span></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {includes.map((inc, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Popover
                  trigger="click"
                  open={iconPickerOpen === i}
                  onOpenChange={(v) => setIconPickerOpen(v ? i : null)}
                  placement="bottomLeft"
                  content={
                    <div style={{ width: 208 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
                        {PACKAGE_ICON_OPTIONS.map((opt) => (
                          <button
                            key={opt.icon}
                            type="button"
                            title={opt.label}
                            onClick={() => { updateIncludeIcon(i, opt.icon); setIconPickerOpen(null); }}
                            style={{
                              width: 44, height: 44, fontSize: 19,
                              border: inc.icon === opt.icon ? "1.5px solid #5b50d6" : "1px solid #e5e2fb",
                              borderRadius: 8,
                              background: inc.icon === opt.icon ? "#f3f1ff" : "#fff",
                              cursor: "pointer", display: "flex",
                              alignItems: "center", justifyContent: "center",
                            }}
                          >
                            {opt.icon}
                          </button>
                        ))}
                      </div>
                      {inc.icon && (
                        <button
                          type="button"
                          onClick={() => { updateIncludeIcon(i, undefined); setIconPickerOpen(null); }}
                          style={{
                            width: "100%", marginTop: 8, height: 32,
                            border: "none", borderRadius: 6, background: "transparent",
                            color: "#dc2626", cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                          }}
                        >
                          Ukloni ikonicu (automatski prepoznaj)
                        </button>
                      )}
                    </div>
                  }
                >
                  <button
                    type="button"
                    title="Odaberi ikonicu"
                    style={{
                      flexShrink: 0, width: 36, height: 36,
                      border: "1px solid #e2e2ea", borderRadius: 9,
                      background: "#fafafa", cursor: "pointer",
                      fontSize: 16, display: "flex",
                      alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {inc.icon || "🎁"}
                  </button>
                </Popover>
                <input
                  className={`am-input${errors.includes ? " am-input-error" : ""}`}
                  value={inc.label}
                  onChange={(e) => updateInclude(i, e.target.value)}
                  placeholder={
                    i === 0 ? "npr. Doručak" :
                    i === 1 ? "npr. Vožnja kvadom" :
                    "npr. Kasna odjava 14h"
                  }
                  style={{ flex: 1 }}
                />
                {includes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeInclude(i)}
                    style={{
                      flexShrink: 0, width: 36, height: 36,
                      border: "1px solid #fecaca", borderRadius: 9,
                      background: "#fff5f5", color: "#dc2626",
                      cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center",
                      fontSize: 18, lineHeight: 1,
                    }}
                  >
                    −
                  </button>
                )}
              </div>
            ))}
            {errors.includes && <span className="am-error-msg">{errors.includes}</span>}
            <button
              type="button"
              onClick={addInclude}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                height: 38, padding: "0 14px",
                border: "1.5px dashed #c4bfef", borderRadius: 9,
                background: "transparent", color: "#5b50d6",
                cursor: "pointer", fontSize: 13, fontWeight: 600,
                fontFamily: "inherit", marginTop: 2,
              }}
            >
              + Dodaj stavku
            </button>
          </div>
        </div>

        {/* ── Cijena ── */}
        <div className="am-section">
          <div className="am-section-label">Cijena</div>
          <div className="am-form-grid">

            <div className="am-field am-span-2">
              <label className="am-label">Tip cijene <span className="am-req">*</span></label>
              <div className="am-select-wrap">
                <select
                  className="am-select"
                  value={formData.priceType}
                  onChange={setField("priceType")}
                >
                  <option value="discount">Procentualni popust na osnovnu cijenu</option>
                  <option value="custom">Fiksna cijena po noći (po broju osoba)</option>
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>

            {formData.priceType === "discount" ? (
              <>
                <div className="am-field">
                  <label className="am-label">Popust (%) <span className="am-req">*</span></label>
                  <input
                    className={`am-input${errors.discountPercent ? " am-input-error" : ""}`}
                    type="number"
                    min={1}
                    max={100}
                    placeholder="npr. 15"
                    value={formData.discountPercent}
                    onChange={setField("discountPercent")}
                  />
                  {errors.discountPercent && <span className="am-error-msg">{errors.discountPercent}</span>}
                </div>

                <div className="am-field">
                  <label className="am-label">Valuta</label>
                  <div className="am-select-wrap">
                    <select className="am-select" value={formData.currency} onChange={setField("currency")}>
                      <option value="EUR">EUR (€)</option>
                      <option value="RSD">RSD</option>
                      <option value="BAM">BAM (KM)</option>
                    </select>
                    <span className="am-chev"><ChevDown /></span>
                  </div>
                </div>
              </>
            ) : (
              <div className="am-field am-span-2">
                <label className="am-label">
                  Cijena po noći prema broju osoba <span className="am-req">*</span>
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {/* Header row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 36px", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Br. osoba</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Cijena/noć</span>
                    <span />
                  </div>
                  {pricingTiers.map((tier, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 36px", gap: 8, alignItems: "center" }}>
                      <input
                        className="am-input"
                        type="number"
                        min={1}
                        placeholder="npr. 4"
                        value={tier.persons}
                        onChange={(e) => setPricingTiers((prev) =>
                          prev.map((t, idx) => idx === i ? { ...t, persons: e.target.value } : t)
                        )}
                      />
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input
                          className={`am-input${errors.pricePerNight ? " am-input-error" : ""}`}
                          type="number"
                          min={0}
                          placeholder="npr. 80"
                          value={tier.pricePerNight}
                          onChange={(e) => {
                            setPricingTiers((prev) =>
                              prev.map((t, idx) => idx === i ? { ...t, pricePerNight: e.target.value } : t)
                            );
                            if (errors.pricePerNight) setErrors((prev) => ({ ...prev, pricePerNight: undefined }));
                          }}
                          style={{ flex: 1 }}
                        />
                        <div className="am-select-wrap" style={{ width: 84, flexShrink: 0 }}>
                          <select className="am-select" value={formData.currency} onChange={setField("currency")}>
                            <option value="EUR">EUR</option>
                            <option value="RSD">RSD</option>
                            <option value="BAM">BAM</option>
                          </select>
                          <span className="am-chev"><ChevDown /></span>
                        </div>
                      </div>
                      {pricingTiers.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => setPricingTiers((prev) => prev.filter((_, idx) => idx !== i))}
                          style={{
                            width: 36, height: 36, flexShrink: 0,
                            border: "1px solid #fecaca", borderRadius: 9,
                            background: "#fff5f5", color: "#dc2626",
                            cursor: "pointer", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            fontSize: 18, lineHeight: 1,
                          }}
                        >−</button>
                      ) : <span />}
                    </div>
                  ))}
                  {errors.pricePerNight && <span className="am-error-msg">{errors.pricePerNight}</span>}
                  <button
                    type="button"
                    onClick={() => setPricingTiers((prev) => [...prev, { persons: "", pricePerNight: "" }])}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      height: 38, padding: "0 14px",
                      border: "1.5px dashed #c4bfef", borderRadius: 9,
                      background: "transparent", color: "#5b50d6",
                      cursor: "pointer", fontSize: 13, fontWeight: 600,
                      fontFamily: "inherit", marginTop: 2,
                    }}
                  >
                    + Dodaj tier
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Pravila za djecu ── */}
        <div className="am-section">
          <div className="am-section-label">
            Pravila za djecu
            <span className="am-field-hint">opciono — definiše cijenu po noći za svaki uzrast</span>
          </div>

          {childrenRules.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "#9893a8", marginBottom: 10 }}>
              Nema pravila — sva djeca se tretiraju kao odrasle osobe.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
              {/* Header */}
              <div style={{ display: "grid", gridTemplateColumns: "80px 80px 150px 1fr 36px", gap: 8, alignItems: "center" }}>
                {["Od god.", "Do god.", "Tip", "Cijena/noć", ""].map((h) => (
                  <span key={h} style={{ fontSize: 10.5, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</span>
                ))}
              </div>
              {childrenRules.map((rule, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "80px 80px 150px 1fr 36px", gap: 8, alignItems: "center" }}>
                  <input
                    className="am-input"
                    type="number" min={0} max={17}
                    placeholder="0"
                    value={rule.ageFrom}
                    onChange={(e) => setChildrenRules((prev) =>
                      prev.map((r, idx) => idx === i ? { ...r, ageFrom: e.target.value } : r)
                    )}
                  />
                  <input
                    className="am-input"
                    type="number" min={1} max={18}
                    placeholder="∞"
                    title="Prazno = bez gornje granice"
                    value={rule.ageTo}
                    onChange={(e) => setChildrenRules((prev) =>
                      prev.map((r, idx) => idx === i ? { ...r, ageTo: e.target.value } : r)
                    )}
                  />
                  <div className="am-select-wrap">
                    <select
                      className="am-select"
                      value={rule.priceType}
                      onChange={(e) => setChildrenRules((prev) =>
                        prev.map((r, idx) => idx === i ? { ...r, priceType: e.target.value as any } : r)
                      )}
                    >
                      <option value="free">Besplatno</option>
                      <option value="fixed">Fiksna cijena</option>
                      <option value="adult">Puna cijena</option>
                    </select>
                    <span className="am-chev"><ChevDown /></span>
                  </div>
                  {rule.priceType === "fixed" ? (
                    <input
                      className="am-input"
                      type="number" min={0}
                      placeholder={`0 ${formData.currency}`}
                      value={rule.pricePerNight}
                      onChange={(e) => setChildrenRules((prev) =>
                        prev.map((r, idx) => idx === i ? { ...r, pricePerNight: e.target.value } : r)
                      )}
                    />
                  ) : (
                    <span style={{ fontSize: 12, color: "#9893a8" }}>
                      {rule.priceType === "free" ? "€0 / noć" : "kao odrasla"}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setChildrenRules((prev) => prev.filter((_, idx) => idx !== i))}
                    style={{
                      width: 36, height: 36, flexShrink: 0,
                      border: "1px solid #fecaca", borderRadius: 9,
                      background: "#fff5f5", color: "#dc2626",
                      cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center",
                      fontSize: 18, lineHeight: 1,
                    }}
                  >−</button>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => setChildrenRules((prev) => [...prev, { ageFrom: "", ageTo: "", priceType: "free", pricePerNight: "" }])}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              height: 38, padding: "0 14px",
              border: "1.5px dashed #c4bfef", borderRadius: 9,
              background: "transparent", color: "#5b50d6",
              cursor: "pointer", fontSize: 13, fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            + Dodaj pravilo za uzrast
          </button>
        </div>

        {/* ── Postavke ── */}
        <div className="am-section">
          <div className="am-section-label">Postavke</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Switch
              checked={formData.isActive}
              onChange={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))}
            />
            <span style={{ fontSize: 13.5, fontWeight: 550, color: formData.isActive ? "#15803d" : "#9893a8" }}>
              {formData.isActive ? "Akcija je aktivna" : "Akcija je neaktivna"}
            </span>
          </div>
        </div>
      </AppModal>
    </div>
  );
};

// ─── Package Card ─────────────────────────────────────────────────────────────

interface CardProps {
  pkg: AptPackage;
  onEdit: (pkg: AptPackage) => void;
  onDelete: (id: string) => void;
  onToggle: (pkg: AptPackage) => void;
  onRestore?: (pkg: AptPackage) => void;
  archived?: boolean;
}

const PackageCard: React.FC<CardProps> = ({ pkg, onEdit, onDelete, onToggle, onRestore, archived }) => {
  const expired  = isExpired(pkg);
  const upcoming = isUpcoming(pkg);
  const today    = dayjs().startOf("day");
  const daysLeft = expired ? 0 : dayjs(pkg.validTo).diff(today, "day");

  return (
    <div style={{
      border: `1.5px solid ${pkg.isActive && !expired ? "rgba(91,80,214,0.2)" : "#e8e5f0"}`,
      borderRadius: 14,
      padding: "16px 16px 14px",
      background: pkg.isActive && !expired ? "linear-gradient(135deg,#f8f7ff 0%,#fff 100%)" : "#fafafa",
      position: "relative",
      overflow: "hidden",
    }}>
      {pkg.isActive && !expired && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3,
          background: "linear-gradient(90deg,#5b50d6,#818cf8)",
          borderRadius: "14px 14px 0 0",
        }} />
      )}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: pkg.isActive && !expired ? "#5b50d6" : "#9893a8", marginBottom: 3 }}>
            {archived ? "Arhivirana" : expired ? "Istekla" : upcoming ? "Nadolazeća" : "Akcija"}
          </div>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1520", lineHeight: 1.3 }}>{pkg.name}</div>
          {pkg.description && (
            <div style={{ fontSize: 11.5, color: "#6b6880", marginTop: 3, lineHeight: 1.4 }}>{pkg.description}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 5, flexShrink: 0, alignItems: "center" }}>
          {!archived && (
            <Tooltip title={pkg.isActive ? "Deaktiviraj" : "Aktiviraj"}>
              <Switch checked={pkg.isActive} size="small" onChange={() => onToggle(pkg)} />
            </Tooltip>
          )}
          <Tooltip title="Izmijeni">
            <button
              onClick={() => onEdit(pkg)}
              style={{ width: 28, height: 28, border: "1px solid #e8e5f0", borderRadius: 7, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b6880" }}
            >
              <EditOutlined style={{ fontSize: 12 }} />
            </button>
          </Tooltip>
          {archived ? (
            <Popconfirm title="Vratiti akciju?" description="Akcija će ponovo biti aktivna i vidljiva gostima." onConfirm={() => onRestore?.(pkg)} okText="Vrati" cancelText="Otkaži">
              <button
                style={{ width: 28, height: 28, border: "1px solid #bbf7d0", borderRadius: 7, background: "#f0fdf4", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}
              >
                <UndoOutlined style={{ fontSize: 12 }} />
              </button>
            </Popconfirm>
          ) : (
            <Popconfirm title="Arhivirati akciju?" description="Akcija neće biti vidljiva gostima, ali ostaje u istoriji rezervacija." onConfirm={() => onDelete(pkg._id)} okText="Arhiviraj" cancelText="Otkaži" okButtonProps={{ danger: true }}>
              <button
                style={{ width: 28, height: 28, border: "1px solid #fecaca", borderRadius: 7, background: "#fff5f5", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}
              >
                <DeleteOutlined style={{ fontSize: 12 }} />
              </button>
            </Popconfirm>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11.5, color: "#6b6880" }}>
          📅 <strong style={{ color: "#1a1520" }}>{fmtDate(pkg.validFrom)}</strong> – <strong style={{ color: "#1a1520" }}>{fmtDate(pkg.validTo)}</strong>
          {!expired && !upcoming && (
            <span style={{ marginLeft: 6, color: daysLeft <= 7 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
              ({daysLeft} {daysLeft === 1 ? "dan" : "dana"} preostalo)
            </span>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: "#6b6880" }}>
          🌙 min. <strong style={{ color: "#1a1520" }}>{pkg.minNights}</strong> {pkg.minNights === 1 ? "noć" : "noći"}
        </div>
        {pkg.apartmentName
          ? <div style={{ fontSize: 11.5, color: "#6b6880" }}>🏠 <strong style={{ color: "#1a1520" }}>{pkg.apartmentName}</strong></div>
          : <div style={{ fontSize: 11.5, color: "#9893a8" }}>🏠 Svi apartmani</div>
        }
      </div>

      {pkg.includes?.filter((i) => i.label).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 10 }}>
          {pkg.includes.filter((i) => i.label).map((inc, idx) => (
            <span key={idx} style={{
              fontSize: 11, fontWeight: 600, padding: "3px 8px",
              borderRadius: 20, background: "rgba(91,80,214,0.08)", color: "#5b50d6",
              border: "1px solid rgba(91,80,214,0.15)",
            }}>
              {inc.icon ? inc.icon : "✓"} {inc.label}
            </span>
          ))}
        </div>
      )}

      <div style={{ borderTop: "1px solid #f0edf8", paddingTop: 10 }}>
        {pkg.pricingByPersons && pkg.pricingByPersons.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
            {[...pkg.pricingByPersons].sort((a, b) => a.persons - b.persons).map((tier, i) => (
              <span key={i} style={{ fontSize: 12.5, color: "#1a1520" }}>
                <span style={{ fontWeight: 400, color: "#9893a8", fontSize: 11 }}>{tier.persons} os. → </span>
                <span style={{ fontWeight: 700 }}>{fmtAmount(tier.pricePerNight, pkg.currency)}</span>
                <span style={{ fontWeight: 400, color: "#9893a8", fontSize: 11 }}>/noć</span>
              </span>
            ))}
          </div>
        ) : pkg.discountPercent != null ? (
          <span style={{ fontSize: 13, fontWeight: 700, color: "#16a34a" }}>−{pkg.discountPercent}% popust</span>
        ) : (
          <span style={{ fontSize: 12, color: "#9893a8" }}>Cijena nije postavljena</span>
        )}

        {pkg.childrenPricing && pkg.childrenPricing.length > 0 && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #f0edf8" }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9893a8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
              👶 Djeca
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 10px" }}>
              {pkg.childrenPricing.map((rule, i) => {
                const range = rule.ageTo != null
                  ? `${rule.ageFrom}–${rule.ageTo} god.`
                  : `${rule.ageFrom}+ god.`;
                const price = rule.priceType === "free"
                  ? "besplatno"
                  : rule.priceType === "adult"
                  ? "puna cijena"
                  : `${fmtAmount(rule.pricePerNight ?? 0, pkg.currency)}/noć`;
                return (
                  <span key={i} style={{ fontSize: 11.5, color: "#6b6880" }}>
                    <span style={{ fontWeight: 600, color: "#1a1520" }}>{range}</span>
                    {" → "}{price}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackagesTab;
